import React, { useEffect, useState } from "react";
import FullCircleChart from "./FullCircleChart";
import { useAuthContext } from "../../../context/AuthContext";
import saveResourceAvailability from "../../../apis/saveResourceAvailability";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../assets/css/availability-modal.css";

const AVAILABILITY_OPTIONS = [40, 30, 20, 10, 5, 0];

const AvailabilityModal = ({
  currentValue,
  resourceSlug,
  currentUserSlug,
  onClose,
  onSave,
}) => {
  const [selected, setSelected] = useState(currentValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setSelected(currentValue);
    onClose();
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const res = await saveResourceAvailability({
        current_user_slug: currentUserSlug,
        resourceSlug,
        availability: selected,
      });

      if (res?.data?.status === 1) {
        toast.success(res.data.message || "Availability updated.");
        onSave(selected);
        onClose();
      } else {
        toast.warning(res?.data?.message || "Something went wrong.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="avail-overlay" onClick={handleClose}>
      <div className="avail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="avail-modal-header">
          <h2 className="avail-modal-title">Edit Your Weekly Availability</h2>
        </div>

        <div className="avail-modal-body">
          <p className="avail-modal-sub">I'm available up to</p>
          <div className="avail-grid">
            {AVAILABILITY_OPTIONS.map((val) => (
              <button
                key={val}
                className={`avail-option${selected === val ? " selected" : ""}${val === 0 ? " not-available" : ""}`}
                onClick={() => setSelected(val)}
                disabled={isSubmitting}
              >
                {val > 0 ? `${val} hrs/week` : "Not Currently Available"}
              </button>
            ))}
          </div>
        </div>

        <div className="avail-modal-footer">
          <button
            className="avail-btn-close"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            className="avail-btn-save"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{ animation: "avail-spin 1s linear infinite" }}
                >
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
      <style>{`@keyframes avail-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const AvailabilityV2 = ({
  availabilityDetails,
  canUpdate = false,
  resourceSlug = false,
}) => {
  const { userDetails } = useAuthContext();
  const current_user_slug =
    typeof userDetails === "string"
      ? JSON.parse(userDetails).user_slug
      : userDetails?.user_slug;

  const [isLoading, setIsLoading] = useState(true);
  const [availability, setAvailability] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (availabilityDetails?.availability !== undefined) {
      setAvailability(parseInt(availabilityDetails.availability) || 0);
      setIsLoading(false);
    }
  }, [availabilityDetails]);

  return (
    <>
      <div className="fw-medium mb-2 d-flex align-items-center justify-content-between">
        <span>Available Hours / Week</span>
        {canUpdate && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            onClick={() => setShowModal(true)}
            style={{
              cursor: "pointer",
              color: "var(--primary)",
              flexShrink: 0,
            }}
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        )}
      </div>

      {!isLoading && (
        <div
          className="d-flex justify-content-center"
          style={{
            position: "relative",
            display: "inline-flex",
            width: "100%",
          }}
        >
          <div
            onClick={() => canUpdate && setShowModal(true)}
            style={{
              position: "relative",
              display: "inline-block",
              cursor: canUpdate ? "pointer" : "default",
              borderRadius: "50%",
            }}
            className={canUpdate ? "avail-chart-wrap" : ""}
          >
            <FullCircleChart value={availability} size={90} />

            {/* Hover overlay — only shown via CSS when canUpdate */}
            {canUpdate && (
              <div className="avail-chart-overlay">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && canUpdate && (
        <AvailabilityModal
          currentValue={availability}
          resourceSlug={resourceSlug}
          currentUserSlug={current_user_slug}
          onClose={() => setShowModal(false)}
          onSave={(newVal) => setAvailability(newVal)}
        />
      )}
    </>
  );
};

export default AvailabilityV2;
